/**
 * Waits for a specified condition to become true within a given timeout period.
 *
 * This function repeatedly checks a condition at specified intervals until it becomes true
 * or until a timeout is reached. It's useful for scenarios where you need to wait for an
 * asynchronous operation to complete or for a certain state to be achieved.
 *
 * @param condition A function that returns a boolean. The waiting will continue until this function returns true.
 * @param timeout The maximum time to wait in milliseconds before throwing a timeout error. Defaults to 300000 (5 minutes).
 * @param checkInterval The interval in milliseconds between condition checks. Defaults to 1000 (1 second).
 *
 * @returns A Promise that resolves when the condition becomes true.
 * @throws Error if the timeout is reached before the condition becomes true.
 *
 * @example
 * // Wait for isDataLoaded to become true, checking every 2 seconds, with a 1-minute timeout
 * await waitForCondition(() => isDataLoaded, 60000, 2000);
 */
export async function waitForCondition(
  condition: () => boolean,
  timeout: number = 300000,
  checkInterval: number = 1000,
): Promise<void> {
  const startTime = Date.now();

  while (!condition()) {
    if (Date.now() - startTime > timeout) {
      throw new Error('Operation timed out');
    }
    await new Promise((resolve) => setTimeout(resolve, checkInterval));
  }
}
